import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAdmin, useAdminAuthStore } from "@charisol-spark/client-core";
import type {
  LoginRequest,
  LoginResponse,
  RespondMfaRequest,
} from "@charisol-spark/client-core";
import { authApi, isMfaChallenge } from "@/lib/api";
import { clearTokens } from "@charisol-spark/client-core";

/**
 * Admin sign-in hook — a trimmed copy of
 * packages/frontend/src/hooks/useAuth.ts without onboarding/persona
 * handling (admins have neither) and without register (admin accounts are
 * provisioned internally, not self-registered).
 */
export function useAuth() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, initialized, setUser, clearUser, setInitialized } =
    useAdminAuthStore();

  const isAuthenticated =
    !!user && !!sessionStorage.getItem("spark_access_token");

  const completeSignIn = useCallback(
    (response: LoginResponse) => {
      // authApi already stored the tokens by the time this fires, so
      // isAdmin() here reflects the ID token this sign-in just produced.
      // Cognito authenticates anyone with a valid staging account —
      // platform_* group membership is separate and this is the first
      // point the admin app can see whether the account has it. Without
      // this check, a valid-but-non-admin sign-in used to land signed in
      // on "/", where AdminGuard would silently bounce it back to
      // /login with no explanation (see AdminGuard's comment).
      if (!isAdmin()) {
        clearTokens();
        queryClient.clear();
        clearUser();
        // The email rides in router state, not the query string: a query
        // param lands in Cloudflare/CDN access logs, browser history and
        // any Referer header the next navigation sends, none of which
        // should see it. State is in-memory only and gone on a reload,
        // which LoginPage handles by falling back to an email-less message.
        navigate("/login?reason=no_role", {
          replace: true,
          state: { email: response.user.email },
        });
        return;
      }

      queryClient.clear();
      setUser({
        userId: response.user.userId,
        email: response.user.email,
        name: response.user.name,
      });
      setInitialized();
      navigate("/");
    },
    [queryClient, setUser, setInitialized, navigate, clearUser]
  );

  const loginMutation = useMutation({
    mutationFn: (data: LoginRequest) => authApi.login(data),
    onSuccess: (response) => {
      if (isMfaChallenge(response)) return;
      completeSignIn(response);
    },
  });

  const respondMfaMutation = useMutation({
    mutationFn: (data: RespondMfaRequest) => authApi.respondMfa(data),
    onSuccess: completeSignIn,
  });

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Clear local state even if the API call fails.
    } finally {
      queryClient.clear();
      clearUser();
      clearTokens();
      navigate("/login");
    }
  }, [clearUser, navigate, queryClient]);

  return {
    user,
    isAuthenticated,
    initialized,
    login: loginMutation,
    respondMfa: respondMfaMutation,
    logout,
  };
}
